import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { round } from 'lodash';
import { flow } from 'lodash/fp';

import type { DrawingPath, FreehandPoint } from './drawingEditorUtils';
import { STROKE_SIZES } from './drawingEditorConstants';
import { getX, getY } from '../../../../common/maths/geometry/dimensionUtil';
import { roundFreehandPoint, translateFreehandPoint } from './perfectFreehandUtils';
import { parseDrawingSvgPathTags, transformSvgPathData, tryTransformSvgPathData } from './utils/drawingSvgPathUtils';
import {
    getDrawingData,
    getWidth,
    getXPosition,
    getYPosition,
} from '../../../../common/elements/utils/elementPropertyUtils';
import type { Point } from '../../../../common/maths/geometry/pointTypes';
import type { Rect } from '../../../../common/maths/geometry/rect/rectTypes';
import type {
    MNElementContent,
    MNDrawingContent,
    MNDrawingPath,
    ImMNElement,
    MNElement,
} from '../../../../common/elements/elementModelTypes';

import DrawingSvgRenderer from './drawingEditorCanvas/DrawingSvgRenderer';
import { asObject } from '../../../../common/utils/immutableHelper';

type DrawingContentInput = NonNullable<MNElementContent['drawing']>;
const DEFAULT_FILLED_BAKED_PATH_SIZE = STROKE_SIZES.MEDIUM;

export const translateAndRoundFreehandPoint = (translation: Point) =>
    flow([(point: FreehandPoint) => translateFreehandPoint(translation)(point), roundFreehandPoint]);

export const getDrawingElementContent = ({
    paths,
    boundingRect,
}: {
    paths: DrawingPath[];
    boundingRect: Rect;
}): MNDrawingContent => {
    if (!paths.length) return {};

    const { top, left, width, height } = boundingRect;

    // Offsets each point so their coordinates will be from the top left point of the
    // combined path bounding rect, rather than the drawing editor's top left point
    const translateAndRound = translateAndRoundFreehandPoint({ x: -left, y: -top });

    const translatedPaths: DrawingPath[] = paths.map((path) => {
        const { points, stroke: _stroke, svgPath, ...rest } = path;

        return {
            ...rest,
            // Baked paths keep coordinates in svgPath; points are only used for live freehand paths.
            points: points.length ? points.map(translateAndRound) : points,
            ...(svgPath
                ? {
                      svgPath: transformSvgPathData({
                          pathData: svgPath,
                          translation: { x: -left, y: -top },
                      }),
                  }
                : {}),
        };
    });

    const svg = ReactDOMServer.renderToStaticMarkup(
        <DrawingSvgRenderer paths={translatedPaths} width={width} height={height} />,
    );

    return {
        paths: [] as MNDrawingPath[],
        svg,
        width,
        height,
    };
};

export const getPathsFromElementContent = ({
    drawing,
    offset,
    scale,
}: {
    drawing: DrawingContentInput;
    offset: Point;
    scale: number;
}): DrawingPath[] =>
    parseDrawingSvgPathTags(drawing.svg)
        .map((parsedPath, index) => {
            const scaledSize = parsedPath.strokeWidth
                ? round(parsedPath.strokeWidth * scale, 2)
                : DEFAULT_FILLED_BAKED_PATH_SIZE;
            const transformedPath = tryTransformSvgPathData({
                pathData: parsedPath.d,
                scale,
                translation: offset,
            });

            if (!transformedPath) return null;

            return {
                id: `svg-path-${index}`,
                points: [],
                size: scaledSize,
                thinning: parsedPath.isStrokeOnly ? 0 : undefined,
                color: parsedPath.color || '#323b4a',
                isStrokeOnly: parsedPath.isStrokeOnly,
                svgPath: transformedPath,
            } as DrawingPath;
        })
        .filter((path): path is DrawingPath => !!path);

export const createInitialPaths = ({
    editedDrawingElement,
    gridSize,
    canvasTranslation,
    canvasOrigin,
}: {
    editedDrawingElement?: ImMNElement | MNElement | null;
    gridSize: number;
    canvasTranslation: Point;
    canvasOrigin: Point;
}): DrawingPath[] => {
    if (!editedDrawingElement) return [];

    const element = asObject<MNElement>(editedDrawingElement);

    const width = getWidth(element);
    const drawing = getDrawingData(element) as DrawingContentInput | null;

    if (!drawing) return [];

    const offset = {
        x: (getXPosition(element) - getX(canvasOrigin)) * gridSize - canvasTranslation.x,
        y: (getYPosition(element) - getY(canvasOrigin)) * gridSize - canvasTranslation.y,
    };

    const drawingWidth = drawing.width || 0;
    const scale = drawingWidth ? (width * gridSize - 2) / drawingWidth : 1;

    return getPathsFromElementContent({
        drawing,
        offset,
        scale,
    });
};
